import {useCallback, useRef} from 'react';

/**
 * Formats a string of raw digit characters into the loose display format used
 * while typing in the time input. Max 4 digits.
 *
 * Examples:
 *   ""     → ""
 *   "1"    → "1"
 *   "12"   → "12"
 *   "120"  → "1:20"
 *   "930"  → "9:30"
 *   "1234" → "12:34"
 */
export function formatTimeDigits(digits: string): string {
  const d = digits.slice(0, 4);
  if (d.length === 0) return '';
  if (d.length === 1) return d;
  if (d.length === 2) return d;
  if (d.length === 3) return `${d.slice(0, 1)}:${d.slice(1)}`;
  return `${d.slice(0, 2)}:${d.slice(2, 4)}`;
}

/**
 * Converts digit-only time input into hours/minutes using intuitive grouping:
 * 1-2 digits = hours, 3 digits = H:MM, 4 digits = HH:MM.
 */
function splitTimeDigits(digits: string) {
  const d = digits.slice(0, 4);

  if (d.length <= 2) {
    return {hours: d, minutes: '00'};
  }

  if (d.length === 3) {
    return {hours: d.slice(0, 1), minutes: d.slice(1)};
  }

  return {hours: d.slice(0, 2), minutes: d.slice(2, 4)};
}

/**
 * Normalizes the typed value on blur into a final H:MM / HH:MM time string.
 * Supports both freeform digits and explicit colon input.
 */
export function normalizeTimeInput(
  rawInput: string,
  onError?: (error: string) => void,
  onPeriodChange?: (period: string) => void,
) {
  const cleanInput = rawInput.trim().replace(/[^\d:]/g, '');

  if (!cleanInput) {
    onError?.('');
    return '';
  }

  let hours = '';
  let minutes = '';

  if (cleanInput.includes(':')) {
    const [rawHours = '', rawMinutes = ''] = cleanInput.split(':');

    hours = rawHours.replace(/\D/g, '');
    minutes = rawMinutes.replace(/\D/g, '');

    if (!hours || hours.length > 2 || minutes.length > 2) {
      onError?.('Invalid time format');
      return '';
    }
  } else {
    const splitValue = splitTimeDigits(cleanInput.replace(/\D/g, ''));

    hours = splitValue.hours;
    minutes = splitValue.minutes;
  }

  const numericHours = parseInt(hours, 10);
  const numericMinutes = parseInt(minutes || '0', 10);

  if (Number.isNaN(numericHours) || Number.isNaN(numericMinutes)) {
    onError?.('Invalid time format');
    return '';
  }

  const clampedHours = Math.min(Math.max(numericHours, 0), 24);
  const clampedMinutes = Math.min(Math.max(numericMinutes, 0), 59);

  let nextHours = String(clampedHours);

  if (clampedHours === 24) {
    nextHours = '12';
    onPeriodChange?.('am');
  } else if (clampedHours > 12) {
    nextHours = String(clampedHours - 12);
    onPeriodChange?.('pm');
  } else if (clampedHours === 0) {
    onPeriodChange?.('am');
  }

  onError?.('');

  return `${nextHours}:${String(clampedMinutes).padStart(2, '0')}`;
}

/**
 * Processes a raw input event value into the loose display format used while
 * typing. Deletion/backspacing is handled by re-deriving the display from the
 * remaining digits.
 */
export function processTimeInput(rawInput: string): string {
  const digits = rawInput.replace(/\D/g, '');
  return formatTimeDigits(digits);
}

/**
 * Formats a pasted string into the final blur-normalized time value.
 */
export function formatPastedTime(raw: string): string {
  return normalizeTimeInput(raw);
}

/**
 * Hook that wires up auto-formatting for a time input (HH:MM).
 *
 * Returns handlers to spread on a <TextField> / <input>:
 *   - inputRef   – ref to the underlying <input> for cursor management
 *   - handleChange – onChange handler
 *   - handlePaste  – onPaste handler (intelligent paste formatting)
 */
export function useTimeInput(
  onChange: (value: string) => void,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawInput = e.target.value;
      const processed = processTimeInput(rawInput);
      onChange(processed);
    },
    [onChange],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const formatted = formatPastedTime(pastedText);
      onChange(formatted);
    },
    [onChange],
  );

  return {inputRef, handleChange, handlePaste};
}
